import React, { forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import SelectSingle from "react-select";

import useDirtyState from "../../../../hooks/useDirtyState";
import customStyles from "../../../../assets/select/customStyles";

function read(model, ...names) {
  for (let i = 0; i < names.length; i += 1) {
    if (model && model[names[i]] !== undefined) return model[names[i]];
  }
  return undefined;
}

function source(image) {
  const value = read(
    image,
    "image",
    "Image",
    "base64PNG",
    "Base64PNG",
    "data",
    "Data",
  );
  if (!value) return null;
  if (String(value).startsWith("data:")) return value;
  return `data:image/png;base64,${value}`;
}

function normalizeImageName(value) {
  return String(value || "")
    .split(/[\\/]/)
    .pop()
    .replace(/\.[^.]+$/, "")
    .trim()
    .toLowerCase();
}

function displayImageName(value) {
  return String(value || "")
    .split(/[\\/]/)
    .pop()
    .replace(/\.[^.]+$/, "");
}

const SmartSignSettingsIcon = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const state = useDirtyState(props.initialSettings);
  const link = localStorage.getItem("link");
  const icons = state.value.defaultImages || [];

  function update(field, value) {
    const newValue = { ...state.value, [field]: value };
    state.update(newValue);
    if (props.onUpdate) props.onUpdate(newValue);
  }

  async function save() {
    if (!state.hasChanges()) return;
    const color = state.value.color.replace("#", "");
    const imageName =
      read(selected, "name", "Name") || state.value.icon;
    await Promise.all([
      axios.put(
        `${link}/api/ImageDisplay/Image?elementId=${state.value.backendId}&configType=${state.value.configType}&imageName=${encodeURIComponent(imageName)}`,
        null,
      ),
      axios.put(
        `${link}/api/ImageDisplay/Color?elementId=${state.value.backendId}&configType=${state.value.configType}&r=${parseInt(color.substring(0, 2), 16)}&g=${parseInt(color.substring(2, 4), 16)}&b=${parseInt(color.substring(4, 6), 16)}`,
        null,
      ),
    ]);
    state.resetDirty();
  }

  useImperativeHandle(ref, () => ({
    getSettings: state.getValue,
    hasChanges: state.hasChanges,
    resetChanges: state.resetDirty,
    isDirty: state.hasChanges,
    save,
    reset: () => state.update(props.initialSettings),
  }));

  const selected = icons.find(
    (icon) =>
      normalizeImageName(read(icon, "name", "Name")) ===
      normalizeImageName(state.value.icon),
  );
  const iconOptions = icons.map((icon, index) => {
    const name =
      read(icon, "name", "Name") ||
      t("smartSign.iconNumber", { number: index + 1 });

    return {
      value: name,
      label: (
        <div className="flex-iac settings-item">
          {displayImageName(name)}
        </div>
      ),
    };
  });

  return (
    <div className="settings-groups">
      <div className="settings-name">{t("smartSign.iconSettings")}</div>
      <div style={boxStyle}>
        <div style={{ display: "flex", gap: "20px" }}>
          <div style={previewBox}>
            {source(selected) ? (
              <img src={source(selected)} alt="" style={previewImage} />
            ) : (
              <span>{t("smartSign.noIconSelected")}</span>
            )}
          </div>

          <div style={controlsStyle}>
            <h4>Symbol</h4>
            <div>
              <label style={{ marginBottom: "0px" }} htmlFor="smart-sign-icon">
                {t("smartSign.icon")}
              </label>
              <SelectSingle
                inputId="smart-sign-icon"
                className="select"
                styles={customStyles}
                isSearchable={false}
                value={
                  iconOptions.find(
                    (option) =>
                      normalizeImageName(option.value) ===
                      normalizeImageName(state.value.icon),
                  ) || null
                }
                onChange={(option) => update("icon", option.value)}
                options={iconOptions}
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 3,
                  colors: { ...theme.colors, primary: "#20a0ff" },
                })}
              />
            </div>

            <div style={{ marginTop: "12px" }}>
              <label
                style={{ marginBottom: "0px" }}
                htmlFor="smart-sign-icon-color"
              >
                {t("smartSign.color")}
              </label>
              <br />
              <input
                id="smart-sign-icon-color"
                type="color"
                value={state.value.color}
                onChange={(event) => update("color", event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const boxStyle = {
  border: "1px solid #ccc",
  padding: "10px",
  borderRadius: "6px",
};

const previewBox = {
  width: "140px",
  height: "140px",
  background: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  flexShrink: 0,
};

const controlsStyle = {
  flex: 1,
  minWidth: 0,
};

const previewImage = {
  maxWidth: "90%",
  maxHeight: "90%",
  objectFit: "contain",
};

export default SmartSignSettingsIcon;
